import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  notificationsApi,
  type AuditListParams,
  type UpdateJourneyContentBody,
} from '../api/notificationsApi';
import { notificationsKeys } from '../queryKeys';
import { humanizeError } from '@/lib/apiClient';
import { toast } from '@/hooks/useToast';
import type {
  AlertSeverity,
  NotificationAlertPolicy,
  NotificationGlobalConfig,
  UserNotificationOverrides,
} from '../types';

// ─── Dashboard ────────────────────────────────────────────────────

export function useDashboardSnapshot(day?: string) {
  return useQuery({
    queryKey: notificationsKeys.dashboardMetrics(day),
    queryFn: () => notificationsApi.getDashboardMetrics(day),
    // No polling — HealthMetricsRollupCron writes the snapshot once every
    // 15 min, so a faster cadence just re-reads the same doc. React-query
    // refetches on tab focus / mount, and the "Refresh now" button forces a
    // fresh rollup, both of which give admins fresh data when they need it.
    staleTime: 5 * 60_000,
  });
}

export function useDashboardTimeseries(days = 14) {
  return useQuery({
    queryKey: notificationsKeys.dashboardTimeseries(days),
    queryFn: () => notificationsApi.getDashboardTimeseries(days),
    staleTime: 5 * 60_000,
  });
}

export interface RecentDecisionsParams {
  limit?: number;
  outcome?: 'send' | 'skip' | 'defer';
  userId?: string;
  opened?: boolean;
  cursor?: string;
}

export function useRecentDecisions(paramsOrLimit: RecentDecisionsParams | number = {}) {
  const params: RecentDecisionsParams =
    typeof paramsOrLimit === 'number' ? { limit: paramsOrLimit } : paramsOrLimit;
  return useQuery({
    queryKey: ['notifications', 'decisions', 'recent', params] as const,
    queryFn: () => notificationsApi.listRecentDecisions(params),
    // No refetchInterval — polling was costing ~250 reads/min/tab once we
    // started hydrating opened state from receipts. React-query still
    // refetches on mount and on window focus, so a manual reload or tab
    // switch always yields fresh data. Use the page's "Refresh now" button
    // for an explicit refresh without leaving the tab.
    staleTime: 30_000,
  });
}

export function useDecisionsTimeseries(params: { days?: number; journeyId?: string } = {}) {
  return useQuery({
    queryKey: notificationsKeys.decisionsTimeseries(params),
    queryFn: () => notificationsApi.getDecisionsTimeseries(params),
    // The endpoint scans up to 50k decisions. 5 min mirrors useDashboardTimeseries
    // and avoids re-aggregating on every page mount.
    staleTime: 5 * 60_000,
  });
}

export function useDecisionDetail(decisionId: string | undefined) {
  return useQuery({
    queryKey: ['notifications', 'decisions', 'detail', decisionId] as const,
    queryFn: () => notificationsApi.getDecisionDetail(decisionId as string),
    enabled: !!decisionId,
    staleTime: 30_000,
  });
}

export function useRefreshDashboardSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.refreshDashboardSnapshot(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsKeys.dashboard() });
      // The 14-day chart on Health is sourced from the live decisions
      // aggregate, not the snapshot rollup — invalidate it too so
      // "Refresh now" actually refreshes everything visible.
      qc.invalidateQueries({ queryKey: ['notifications', 'decisions', 'timeseries'] });
      toast({ title: 'Rollup refreshed', variant: 'success' });
    },
    onError: (err) =>
      toast({
        title: 'Refresh failed',
        description: humanizeError(err),
        variant: 'destructive',
      }),
  });
}

// ─── Config ───────────────────────────────────────────────────────

export function useNotificationConfig() {
  return useQuery({
    queryKey: notificationsKeys.config(),
    queryFn: () => notificationsApi.getConfig(),
    staleTime: 5 * 60_000,
  });
}

export function useUpdateNotificationConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string; patch: Partial<NotificationGlobalConfig> }) =>
      notificationsApi.updateConfig(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsKeys.config() });
      qc.invalidateQueries({ queryKey: notificationsKeys.audit() });
      toast({ title: 'Config updated', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Config update failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

// ─── Journeys ─────────────────────────────────────────────────────

export function useJourneys() {
  return useQuery({
    queryKey: notificationsKeys.journeys(),
    queryFn: () => notificationsApi.listJourneys(),
    staleTime: 60_000,
  });
}

export function useKillJourney() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ journeyId, reason }: { journeyId: string; reason: string }) =>
      notificationsApi.killJourney(journeyId, reason),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: notificationsKeys.journeys() });
      qc.invalidateQueries({ queryKey: notificationsKeys.journey(vars.journeyId) });
      qc.invalidateQueries({ queryKey: notificationsKeys.audit() });
      toast({ title: `Journey ${vars.journeyId} killed`, variant: 'success' });
    },
    onError: (err) => toast({ title: 'Kill failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useReviveJourney() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ journeyId, reason }: { journeyId: string; reason?: string }) =>
      notificationsApi.reviveJourney(journeyId, reason),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: notificationsKeys.journeys() });
      qc.invalidateQueries({ queryKey: notificationsKeys.journey(vars.journeyId) });
      qc.invalidateQueries({ queryKey: notificationsKeys.audit() });
      toast({ title: `Journey ${vars.journeyId} revived`, variant: 'success' });
    },
    onError: (err) => toast({ title: 'Revive failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useUpdateJourneyContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      journeyId,
      body,
    }: {
      journeyId: string;
      body: UpdateJourneyContentBody;
    }) => notificationsApi.updateJourneyContent(journeyId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsKeys.journeys() });
      toast({ title: 'Journey content saved', variant: 'success' });
    },
    onError: (err) =>
      toast({
        title: 'Save failed',
        description: humanizeError(err),
        variant: 'destructive',
      }),
  });
}

// ─── User overrides ───────────────────────────────────────────────

export function useUserNotificationProfile(userId: string | undefined) {
  return useQuery({
    queryKey: notificationsKeys.userProfile(userId ?? ''),
    queryFn: () => notificationsApi.getUserProfile(userId as string),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useSetUserOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      reason,
      overrides,
    }: {
      userId: string;
      reason: string;
      overrides: Partial<UserNotificationOverrides>;
    }) => notificationsApi.setUserOverride(userId, { reason, overrides }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: notificationsKeys.userProfile(vars.userId) });
      qc.invalidateQueries({ queryKey: notificationsKeys.audit() });
      toast({ title: 'Override saved', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Override failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useClearUserOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      notificationsApi.clearUserOverride(userId, reason),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: notificationsKeys.userProfile(vars.userId) });
      qc.invalidateQueries({ queryKey: notificationsKeys.audit() });
      toast({ title: 'Override cleared', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Clear failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

// ─── Alerts ───────────────────────────────────────────────────────

export function useAlertPolicies() {
  return useQuery({
    queryKey: notificationsKeys.alertPolicies(),
    queryFn: () => notificationsApi.listAlertPolicies(),
    staleTime: 60_000,
  });
}

export function useUpdateAlertPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      policyKey,
      reason,
      patch,
    }: {
      policyKey: string;
      reason: string;
      patch: Partial<NotificationAlertPolicy>;
    }) => notificationsApi.updateAlertPolicy(policyKey, { reason, patch }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsKeys.alertPolicies() });
      qc.invalidateQueries({ queryKey: notificationsKeys.audit() });
      toast({ title: 'Policy updated', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Policy update failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useAlertFirings(filters: {
  policyKey?: string;
  severity?: AlertSeverity;
  since?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: notificationsKeys.alertFirings(filters),
    queryFn: () => notificationsApi.listFirings(filters),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useAcknowledgeFiring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ firingId, note }: { firingId: string; note?: string }) =>
      notificationsApi.acknowledgeFiring(firingId, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsKeys.alerts() });
      qc.invalidateQueries({ queryKey: notificationsKeys.audit() });
      toast({ title: 'Firing acknowledged', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Ack failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

// ─── Audit ────────────────────────────────────────────────────────

export function useAuditList(params: AuditListParams) {
  return useQuery({
    queryKey: notificationsKeys.audit(params),
    queryFn: () => notificationsApi.listAudit(params),
    staleTime: 30_000,
  });
}
